import { useCallback, useEffect, useMemo } from 'react';
import { SelectOption, SidebarDateFilter, SidebarSelectFilter } from '../../../components';
import { AVERAGE_BY_OPTIONS } from '../../../constants';
import { useAppDispatch, useAppSelector, useGetFiltersFromParams, useUpdateFilterParams } from '../../../hooks';
import { setAnalyticsFilters, updateAnalyticsFilters } from '../../../redux/reducers';
import { useGetTeamsQuery, useGetUsersForSelectQuery, useGetUserTeamsQuery } from '../../../services';
import {
  AnalyticsFilterKeys,
  AnalyticsFilters as AnalyticsFiltersType,
  AnalyticsTab,
  FilterValues,
  RoleFilterType,
  Roles,
} from '../../../types';
import { getAnalyticsActiveTab, getTeamsOptions, getUserOptions, isFiltersDirty } from '../../../utils';
import SidebarFilters from '../SidebarFilters';

interface AnalyticsFiltersProps {
  isTagsLoading: boolean;
  isProspectsLoading: boolean;
  tagOptions: SelectOption[];
  prospectOptions: SelectOption[];
}

const AnalyticsFilters = ({
  tagOptions,
  prospectOptions,
  isTagsLoading,
  isProspectsLoading,
}: AnalyticsFiltersProps) => {
  const activeTab = getAnalyticsActiveTab();
  const isProgressReportTab = activeTab === AnalyticsTab.PROGRESS_REPORT;
  const isTeamPerformanceTab = activeTab === AnalyticsTab.TEAM_PERFORMANCE;

  // Redux
  const dispatch = useAppDispatch();
  const savedFilters = useAppSelector((state) => state.filters.analyticsFilters[activeTab]);
  const user = useAppSelector((state) => state.auth.user);

  // Custom hooks
  const filters = useGetFiltersFromParams() as AnalyticsFiltersType;
  const updateFilterParams = useUpdateFilterParams();

  // Queries
  // In activity tab we show all org analytics so we include all roles in user select
  // In progress report tab we show lower hierarchy roles
  const { data: userData, isLoading: isLoadingUsers } = useGetUsersForSelectQuery(
    isProgressReportTab
      ? { includeCurrentUser: true, includeDisabledUsers: true }
      : {
          roleFilterType: RoleFilterType.SPECIFY_ROLES,
          roles: [Roles.MANAGER, Roles.ADMIN, Roles.SALES_REP],
          includeDisabledUsers: true,
        }
  );
  const { data: orgTeamsData = [], isLoading: isLoadingOrgTeams } = useGetTeamsQuery();
  const { data: userTeamsData = [] } = useGetUserTeamsQuery(user?.id ?? '', { skip: !user?.id });

  const userOptions = useMemo(() => getUserOptions({ users: userData?.users || [] }), [userData]);

  const teamOptions = useMemo(() => getTeamsOptions(orgTeamsData, userTeamsData), [orgTeamsData, userTeamsData]);

  const updateFilter = useCallback(
    (key: AnalyticsFilterKeys) => (value?: FilterValues) => {
      dispatch(updateAnalyticsFilters({ [key]: value, tab: activeTab }));
      updateFilterParams({ [key]: value });
    },
    [dispatch, activeTab, updateFilterParams]
  );

  // Disable the team and user filters if the other filter has values.
  const isTeamFilterDisabled = !!filters.callers;
  const isUserFilterDisabled = !!filters.teamIds;

  const sharedUserFilterProps = {
    options: userOptions,
    placeholder: 'Select user',
    title: 'User',
    loading: isLoadingUsers,
  };

  const renderDateRangeFilter = () => (
    <SidebarDateFilter
      title="Date range"
      onChange={updateFilter(AnalyticsFilterKeys.DATE_RANGE)}
      selected={filters.dateRange}
      disableClear
    />
  );

  // This effect checks if there are any filters applied on component mount.
  useEffect(() => {
    const isDirty = isFiltersDirty(filters);
    if (isDirty) {
      // If filters are applied, update the Redux store with the current filters from the URL.
      dispatch(setAnalyticsFilters({ tab: activeTab, ...filters }));
    } else {
      // If no filters are applied, restore the filters from saved settings.
      updateFilterParams(savedFilters);
    }
    // We purposely do not add the filters to the dependency array because we do not want to re-run this effect when the filters change.
  }, [updateFilterParams]);

  // Render only date range filter for team performance tab.
  if (isTeamPerformanceTab) return renderDateRangeFilter();

  return (
    <SidebarFilters>
      {renderDateRangeFilter()}
      {!isProgressReportTab && (
        <>
          {/* Only show the Team filter if there are team options */}
          {!!teamOptions.length && (
            <SidebarSelectFilter
              options={teamOptions}
              placeholder="Select teams"
              selected={filters.teamIds}
              title="Team"
              onChange={updateFilter(AnalyticsFilterKeys.TEAM)}
              loading={isLoadingOrgTeams}
              disabled={isTeamFilterDisabled}
              tooltip={isTeamFilterDisabled ? 'Remove user filter to enable' : undefined}
            />
          )}
          <SidebarSelectFilter
            selected={filters.callers}
            onChange={updateFilter(AnalyticsFilterKeys.CALLERS)}
            disabled={isUserFilterDisabled}
            tooltip={isUserFilterDisabled ? 'Remove team filter to enable' : undefined}
            {...sharedUserFilterProps}
          />
        </>
      )}
      {isProgressReportTab && (
        <>
          <SidebarSelectFilter
            selected={filters.userId?.[0]}
            error={!filters.userId}
            onChange={updateFilter(AnalyticsFilterKeys.USER)}
            multiSelect={false}
            {...sharedUserFilterProps}
          />
          <SidebarSelectFilter
            options={AVERAGE_BY_OPTIONS}
            selected={filters.groupBy?.[0]}
            title="Average by"
            onChange={updateFilter(AnalyticsFilterKeys.AVERAGE_BY)}
            multiSelect={false}
          />
        </>
      )}
      <SidebarSelectFilter
        loading={isProspectsLoading}
        options={prospectOptions}
        placeholder="Select simulation"
        selected={filters.prospectIds}
        title="Simulation"
        onChange={updateFilter(AnalyticsFilterKeys.PROSPECT)}
      />
      <SidebarSelectFilter
        loading={isTagsLoading}
        options={tagOptions}
        placeholder="Select tag"
        selected={filters.tags}
        title="Tags"
        onChange={updateFilter(AnalyticsFilterKeys.TAGS)}
      />
    </SidebarFilters>
  );
};

export default AnalyticsFilters;
